import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { TodoNoteCategoryDraft, TodoNoteItemDraft } from '../../types/todoNote';
import { SortableRow } from './TodoNoteSortableRow';

export interface TodoNoteItemAddModalProps {
  open: boolean;
  draft: TodoNoteItemDraft | null;
  categories: TodoNoteCategoryDraft[];
  categoryInput: string;
  saving?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  discardConfirmOpen?: boolean;
  onDismissBackdrop: () => void;
  onCategoryInput: (value: string) => void;
  onPickCategory: (id: string | null) => void;
  onDueDate: (iso: string) => void;
  onDueSwitchToMonthOnly: (iso: string) => void;
  onDueSwitchToDay: (iso: string) => void;
  onMemoPatch: (title: string, body: string) => void;
  onMisc: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
  onDiscardConfirmClose?: () => void;
  onDiscardConfirmDismiss?: () => void;
  usePortal?: boolean;
  title?: string;
  ariaLabel?: string;
  autoFocusMemo?: boolean;
  /** TODO 노트: 입력 블럭 4개를 1행으로 배치 */
  editorRowLayout?: boolean;
  /** 수정 모달: 타이틀 왼쪽 완료 체크 원형 버튼 */
  showHeaderCompleteToggle?: boolean;
  onToggleComplete?: () => void;
  completeMark?: 'v' | 'x' | null;
  /** 수정 모달: 타이틀 오른쪽 휴지통을 길게 눌러 삭제 확인 */
  onDelete?: () => void;
}

const TodoNoteItemAddModal: React.FC<TodoNoteItemAddModalProps> = ({
  open,
  draft,
  categories,
  categoryInput,
  saving = false,
  saveLabel = '저장',
  cancelLabel = '취소',
  discardConfirmOpen = false,
  onDismissBackdrop,
  onCategoryInput,
  onPickCategory,
  onDueDate,
  onDueSwitchToMonthOnly,
  onDueSwitchToDay,
  onMemoPatch,
  onMisc,
  onCancel,
  onSave,
  onDiscardConfirmClose,
  onDiscardConfirmDismiss,
  usePortal = false,
  title = 'TODO 추가',
  ariaLabel,
  autoFocusMemo = true,
  editorRowLayout = false,
  showHeaderCompleteToggle = false,
  onToggleComplete,
  completeMark = null,
  onDelete,
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) setDeleteConfirmOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      window.setTimeout(() => {
        if (!document.querySelector('.cp-todo-note-item-add-overlay')) return;
        if (document.querySelector('.cp-todo-note-item-add-delete-dialog')) {
          setDeleteConfirmOpen(false);
          return;
        }
        if (document.querySelector('.cp-todo-note-item-add-discard-dialog')) {
          onDiscardConfirmClose?.();
          return;
        }
        const row = document.querySelector('.cp-todo-note-item-add-overlay .cp-todo-note-editor-blocks');
        if (row?.closest('.cp-todo-note-row--due-picker-open')) return;
        if (row?.closest('.cp-todo-note-row--cat-menu-open')) return;
        onDismissBackdrop();
      }, 0);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onDismissBackdrop, onDiscardConfirmClose]);

  if (!open || !draft) return null;

  const modal = (
    <div
      className="cp-todo-note-item-add-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? title}
    >
      <div
        className={
          'cp-todo-note-item-add-stack' + (editorRowLayout ? ' cp-todo-note-item-add-stack--editor-row' : '')
        }
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cp-todo-note-item-add-stack-head">
          <div className="cp-todo-note-item-add-header-side is-start">
            {showHeaderCompleteToggle ? (
              <button
                type="button"
                className={`cp-todo-note-item-add-header-complete-row${draft.completed ? ' is-done' : ''}${
                  completeMark === 'x' ? ' is-auto-missed' : ''
                }`}
                aria-pressed={draft.completed}
                aria-label={draft.completed ? '완료 해제' : '완료 체크'}
                onClick={() => onToggleComplete?.()}
              >
                <span
                  className={`cp-todo-note-item-add-header-complete${draft.completed ? ' is-done' : ''}${
                    completeMark === 'x' ? ' is-auto-missed' : ''
                  }`}
                  aria-hidden
                >
                  {draft.completed ? (
                    completeMark === 'x' ? (
                      <svg
                        className="cp-todo-note-complete-mark cp-todo-note-complete-mark--x"
                        viewBox="0 0 20 20"
                        width="14"
                        height="14"
                        aria-hidden
                        focusable="false"
                      >
                        <path
                          d="M5.5 5.5 L14.5 14.5 M14.5 5.5 L5.5 14.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.15"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="cp-todo-note-complete-mark"
                        viewBox="0 0 20 20"
                        width="14"
                        height="14"
                        aria-hidden
                        focusable="false"
                      >
                        <path
                          d="M5.05 10.35 L9.25 14.65 L15.35 5.55"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.15"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )
                  ) : null}
                </span>
                <span className="cp-todo-note-item-add-header-complete-label" aria-hidden>
                  완료 체크
                </span>
              </button>
            ) : null}
          </div>
          <h3 className="cp-todo-note-item-add-stack-title">{title}</h3>
          <div className="cp-todo-note-item-add-header-side is-end">
            {onDelete ? (
              <button
                type="button"
                className="cp-todo-note-item-add-header-delete"
                disabled={saving}
                title="삭제"
                aria-label="TODO 삭제"
                onClick={() => setDeleteConfirmOpen(true)}
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/Trash.svg`}
                  alt=""
                  width={16}
                  height={16}
                />
              </button>
            ) : null}
          </div>
        </div>
        <div className="cp-todo-note-item-add-block">
          <div className="cp-todo-note-item-add-modal-inner">
            <DndContext sensors={sensors}>
              <SortableContext items={[draft.id]} strategy={verticalListSortingStrategy}>
                <SortableRow
                  item={draft}
                  ord={0}
                  categories={categories}
                  categoryInput={categoryInput}
                  onCategoryInput={onCategoryInput}
                  onPickCategory={onPickCategory}
                  onDueDate={onDueDate}
                  onDueSwitchToMonthOnly={onDueSwitchToMonthOnly}
                  onDueSwitchToDay={onDueSwitchToDay}
                  onMemoPatch={onMemoPatch}
                  onMisc={onMisc}
                  onToggleComplete={() => {}}
                  onRemove={() => {}}
                  dragDisabled
                  selected={false}
                  onSelect={() => {}}
                  editorMode
                  autoFocusMemo={autoFocusMemo}
                />
              </SortableContext>
            </DndContext>
          </div>
        </div>
        <div className="cp-todo-note-item-add-actions">
          <button type="button" className="cp-btn-secondary" onClick={onCancel} disabled={saving}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            onClick={onSave}
            disabled={saving || !draft.due_date}
          >
            {saving ? '저장 중…' : saveLabel}
          </button>
        </div>
      </div>
      {deleteConfirmOpen ? (
        <div
          className="cp-todo-note-item-add-discard-dialog cp-todo-note-item-add-delete-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-todo-note-item-add-delete-title"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <h3 id="cp-todo-note-item-add-delete-title">이 TODO를 삭제할까요?</h3>
          <p className="cp-todo-note-item-add-discard-desc">삭제하면 목록에서 제거됩니다.</p>
          <div className="cp-todo-note-item-add-discard-actions">
            <button type="button" className="cp-btn-secondary" onClick={() => setDeleteConfirmOpen(false)}>
              취소
            </button>
            <button
              type="button"
              className="cp-btn-primary"
              onClick={() => {
                setDeleteConfirmOpen(false);
                onDelete?.();
              }}
              disabled={saving}
            >
              삭제
            </button>
          </div>
        </div>
      ) : null}
      {discardConfirmOpen ? (
        <div
          className="cp-todo-note-item-add-discard-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-todo-note-item-add-discard-title"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <h3 id="cp-todo-note-item-add-discard-title">입력 내용을 버리고 닫을까요?</h3>
          <p className="cp-todo-note-item-add-discard-desc">저장하지 않은 내용은 사라집니다.</p>
          <div className="cp-todo-note-item-add-discard-actions">
            <button type="button" className="cp-btn-secondary" onClick={onDiscardConfirmClose}>
              계속 작성
            </button>
            <button type="button" className="cp-btn-primary" onClick={onDiscardConfirmDismiss}>
              닫기
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  if (usePortal) return createPortal(modal, document.body);
  return modal;
};

export default TodoNoteItemAddModal;
